import '@awesome.me/webawesome/dist/components/spinner/spinner.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import '../components/breadcrumb.js';
import '../components/icon.js';

import { faCircleCheck, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { getUserInfo } from '../libraries/auth.js';
import { type DisplayPreferenceKey, saveDisplaySettings } from '../libraries/display_settings.js';
import { DisplaySettingsController } from '../libraries/display_settings_controller.js';
import { checkedElementFromEvent, readEventChecked } from '../libraries/event_target.js';
import { formatDisplayName } from '../libraries/user.js';

type SettingKey = DisplayPreferenceKey;
type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface IDisplaySettingRow {
    key: SettingKey;
    label: string;
    hint: string;
}

const savedIndicatorTimeoutMs = 2000;
const saveErrorMessage = 'Die Einstellung konnte nicht gespeichert werden.';

// The card's rows in display order. Each switch carries its key in `data-setting`
// rather than being identified by its position, so one handler serves every row
// and adding a third cannot silently rewire the existing two.
const displaySettingRows: ReadonlyArray<IDisplaySettingRow> = [
    {
        key: 'hideInactive',
        label: 'Inaktive Organisationen und Betriebe ausblenden',
        hint: 'Inaktive Einträge werden in allen Listen und Auswahlen ausgeblendet.',
    },
    {
        key: 'hideInactiveParcels',
        label: 'Inaktive Schläge ausblenden',
        hint: 'Die Schlagkartei und die Bodenuntersuchung eines Düngejahres zeigen dann nur aktive ' +
            'und nicht aufzeichnungspflichtige Schläge. Inaktive Schläge lassen sich weiterhin über ' +
            'den Status-Filter der Liste einblenden; die übrigen Bereiche zeigen ohnehin nur aktive Schläge.',
    },
];

/**
 * Narrows a `data-setting` attribute to a key the card knows.
 * @param value - The attribute value, absent when the handler is mis-bound.
 * @returns Whether the value names one of the rows.
 */
function isSettingKey (value: string | undefined): value is SettingKey {
    return displaySettingRows.some(row => row.key === value);
}

/**
 * "Einstellungen" page reachable from the user menu. Hosts the per-account
 * display preferences of `libraries/display_settings.ts` in an "Anzeige" card,
 * one switch per preference.
 *
 * Each switch saves pessimistically and reflects the outcome in a persistent
 * status region to the right of its own label: a spinner while saving, a
 * confirmation when saved, and the full error message (with the switch reverted)
 * as visible text on failure. The region is always present so toggling it never
 * reflows the surrounding content, and the state is kept per row so a save on one
 * switch reports nothing on the other.
 */
@customElement('fm-settings')
export class FmSettings extends LitElement {

    static styles = css`
        :host {
            display: block;
        }

        h1 {
            margin: 0 0 var(--wa-space-l) 0;
            font-size: var(--wa-font-size-l);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-normal);
        }

        .card {
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
            padding: var(--wa-space-l);
            background: var(--fm-color-surface);
            border: var(--wa-border-width-s) solid var(--fm-color-border);
            border-radius: var(--fm-border-radius-card);
        }

        h2 {
            margin: 0;
            font-size: var(--wa-font-size-m);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-normal);
        }

        .setting {
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
        }

        .setting-row {
            display: flex;
            align-items: center;
            gap: var(--wa-space-s);
        }

        .status {
            display: inline-flex;
            align-items: center;
            gap: var(--wa-space-xs);
            font-size: var(--wa-font-size-s);
            line-height: 1;
            color: var(--fm-color-text-quiet);
            white-space: nowrap;
        }

        .status--error {
            color: var(--fm-color-danger);
            white-space: normal;
            line-height: 1.4;
        }

        @keyframes fm-status-fade-in {
            from {
                opacity: 0;
            }
        }

        .status--saved,
        .status--error {
            animation: fm-status-fade-in 150ms ease-out;
        }

        .status--saving {
            animation: fm-status-fade-in 200ms ease-out 200ms backwards;
        }

        @media (prefers-reduced-motion: reduce) {
            .status--saved,
            .status--error,
            .status--saving {
                animation: none;
            }
        }

        .hint {
            margin: 0;
            font-size: var(--wa-font-size-s);
            line-height: 1.5;
            color: var(--fm-color-text-quiet);
        }
    `;

    private readonly displaySettings = new DisplaySettingsController(this);

    private readonly userDisplayName = formatDisplayName(getUserInfo());

    @state()
    private saveStates: Record<SettingKey, SaveState> = {
        hideInactive: 'idle',
        hideInactiveParcels: 'idle',
    };

    private setSaveState (key: SettingKey, saveState: SaveState): void {
        this.saveStates = {
            ...this.saveStates,
            [key]: saveState,
        };
    }

    // Everything the event carries is read before the first `await`: dispatch has
    // finished by the time the save resolves, and the event no longer resolves to
    // its target then.
    private readonly handleSettingChange = async (event: Event): Promise<void> => {
        const toggle = checkedElementFromEvent(event);
        const key = toggle.dataset.setting;
        const target = readEventChecked(event);

        if (!isSettingKey(key)) {
            return;
        }

        const previous = this.displaySettings.preference(key);

        this.setSaveState(key, 'saving');
        const saved = await saveDisplaySettings({
            [key]: target,
        });

        if (!saved) {
            toggle.checked = previous;
            this.setSaveState(key, 'error');

            return;
        }

        this.setSaveState(key, 'saved');
        window.setTimeout(() => {
            if (this.saveStates[key] === 'saved') {
                this.setSaveState(key, 'idle');
            }
        }, savedIndicatorTimeoutMs);
    };

    private renderSaveStatusContent (saveState: SaveState): TemplateResult | typeof nothing {
        if (saveState === 'saving') {
            return html`
                <wa-spinner></wa-spinner>
                Wird gespeichert …
            `;
        }

        if (saveState === 'saved') {
            return html`
                <fm-icon .icon=${faCircleCheck}></fm-icon>
                Gespeichert
            `;
        }

        if (saveState === 'error') {
            return html`
                <fm-icon .icon=${faCircleExclamation}></fm-icon>
                ${saveErrorMessage}
            `;
        }

        return nothing;
    }

    private statusClass (saveState: SaveState): string {
        if (saveState === 'idle') {
            return 'status';
        }

        return `status status--${saveState}`;
    }

    private renderSetting (row: IDisplaySettingRow): TemplateResult {
        const saveState = this.saveStates[row.key];

        return html`
            <div class="setting">
                <div class="setting-row">
                    <wa-switch data-setting=${row.key}
                               ?checked=${this.displaySettings.preference(row.key)}
                               ?disabled=${saveState === 'saving'}
                               @change=${this.handleSettingChange}>
                        ${row.label}
                    </wa-switch>
                    <span class=${this.statusClass(saveState)}
                          role="status"
                          aria-live="polite">
                        ${this.renderSaveStatusContent(saveState)}
                    </span>
                </div>
                <p class="hint">${row.hint}</p>
            </div>
        `;
    }

    render (): TemplateResult {
        return html`
            <fm-breadcrumb .items=${[
                {
                    label: this.userDisplayName,
                },
            ]}></fm-breadcrumb>
            <h1>Einstellungen</h1>

            <section class="card">
                <h2>Anzeige</h2>
                ${displaySettingRows.map(row => this.renderSetting(row))}
            </section>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-settings': FmSettings;
    }
}
